import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import api from '../services/api';
import { useToast } from '../hooks/useToast';

const التقاريروالإحصائيات = () => {
  const [growthData, setGrowthData] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [topRestaurants, setTopRestaurants] = useState([]);
  const [topMeals, setTopMeals] = useState([]);
  const [peakHours, setPeakHours] = useState([]);
  const [topAreas, setTopAreas] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('النمو');
  const [dateRange, setDateRange] = useState({
    fromDate: '',
    toDate: '',
  });
  const { showToast, ToastContainer } = useToast();

  const COLORS = ['#E96A3A', '#3FB984', '#4A90E2', '#FFA500', '#9B59B6', '#E74C3C'];

  useEffect(() => {
    fetchReports();
  }, [dateRange]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const [growthResponse, salesResponse, topRestaurantsRes, topMealsRes, peakHoursRes, topAreasRes, activeUsersRes] = await Promise.all([
        api.getGrowthReport(6),
        api.getSalesReport(6),
        api.getTopRestaurants('orders', 10, dateRange.fromDate || undefined, dateRange.toDate || undefined),
        api.getTopMeals(10, dateRange.fromDate || undefined, dateRange.toDate || undefined),
        api.getPeakHours(dateRange.fromDate || undefined, dateRange.toDate || undefined),
        api.getTopAreas(10, dateRange.fromDate || undefined, dateRange.toDate || undefined),
        api.getActiveUsers(10, dateRange.fromDate || undefined, dateRange.toDate || undefined),
      ]);

      // Handle growth data - response can be { data: [...] } or directly an array
      if (growthResponse) {
        const data = Array.isArray(growthResponse) ? growthResponse : (growthResponse.data || []);
        if (data && data.length > 0) {
          // Transform data for chart
          const transformed = data.map(item => ({
            month: item.month,
            users: item.users || 0,
            restaurants: item.restaurants || 0,
            stores: item.stores || 0,
            total: (item.users || 0) + (item.restaurants || 0) + (item.stores || 0),
          }));
          setGrowthData(transformed);
        } else {
          setGrowthData([]);
        }
      } else {
        setGrowthData([]);
      }

      // Handle sales data
      if (salesResponse) {
        const data = Array.isArray(salesResponse) ? salesResponse : (salesResponse.data || []);
        setSalesData(data && data.length > 0 ? data : []);
      } else {
        setSalesData([]);
      }

      // Handle advanced reports
      setTopRestaurants(Array.isArray(topRestaurantsRes) ? topRestaurantsRes : []);
      setTopMeals(Array.isArray(topMealsRes) ? topMealsRes : []);
      setPeakHours(Array.isArray(peakHoursRes) ? peakHoursRes : []);
      setTopAreas(Array.isArray(topAreasRes) ? topAreasRes : []);
      setActiveUsers(Array.isArray(activeUsersRes) ? activeUsersRes : []);
    } catch (error) {
      console.error('Error fetching reports:', error);
      showToast('حدث خطأ في جلب التقارير', 'error');
      setGrowthData([]);
      setSalesData([]);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      showToast('لا توجد بيانات للتصدير', 'error');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => {
        const value = row[header];
        return typeof value === 'string' ? `"${value}"` : value;
      }).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('تم تصدير البيانات بنجاح', 'success');
  };

  const exportToPDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const date = new Date().toLocaleDateString('ar-SA');
    const sections = [
      { title: 'أفضل المطاعم', data: topRestaurants, keys: ['name', 'totalOrders', 'totalRevenue'] },
      { title: 'أفضل الوجبات', data: topMeals, keys: ['name', 'totalOrders', 'totalRevenue'] },
      { title: 'المناطق الأكثر طلباً', data: topAreas, keys: ['area', 'totalOrders'] },
    ];

    const tableHTML = sections.map(({ title, data, keys }) => {
      if (!data || data.length === 0) return '';
      const headers = keys.join('</th><th>');
      const rows = data.map(row =>
        `<tr>${keys.map(k => `<td>${row[k] ?? '-'}</td>`).join('')}</tr>`
      ).join('');
      return `<h2>${title}</h2><table border="1" cellpadding="8" cellspacing="0" style="width:100%;border-collapse:collapse;margin-bottom:24px"><thead><tr><th>${headers}</th></tr></thead><tbody>${rows}</tbody></table>`;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html><html dir="rtl"><head>
      <meta charset="utf-8"><title>تقارير من بيتي — ${date}</title>
      <style>body{font-family:Arial,sans-serif;padding:32px}h1{color:#E96A3A}h2{margin-top:24px}th{background:#E96A3A;color:#fff}td,th{padding:8px;border:1px solid #ddd}tr:nth-child(even){background:#f9f9f9}</style>
      </head><body>
      <h1>تقارير من بيتي</h1><p>تاريخ التصدير: ${date}</p>
      ${tableHTML}
      </body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
  };

  return (
    <div style={{ 
      width: '100%', 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: '#2B2F3F',
      overflow: 'hidden',
      direction: 'rtl'
    }}>
      <Header />
      <div style={{ 
        flex: 1, 
        padding: '32px', 
        backgroundColor: '#2B2F3F', 
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        boxSizing: 'border-box'
      }}>
        {/* Page Title and Controls */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h1 style={{
            fontFamily: 'El Messiri, sans-serif',
            fontSize: '24px',
            fontWeight: 700,
            color: '#FFFFFF',
            margin: 0,
            textAlign: 'right'
          }}>
            التقارير والإحصائيات
          </h1>
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <input
              type="date"
              value={dateRange.fromDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, fromDate: e.target.value }))}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(102, 106, 122, 0.22)',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '14px',
                outline: 'none'
              }}
            />
            <input
              type="date"
              value={dateRange.toDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, toDate: e.target.value }))}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(102, 106, 122, 0.22)',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '14px',
                outline: 'none'
              }}
            />
            <button
              onClick={() => {
                setDateRange({ fromDate: '', toDate: '' });
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid rgba(102, 106, 122, 0.22)',
                backgroundColor: '#FFFFFF',
                color: '#666A7A',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              إعادة تعيين
            </button>
            <button
              onClick={exportToPDF}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#E96A3A',
                color: '#FFFFFF',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              تصدير PDF
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '16px',
          flexWrap: 'wrap'
        }}>
          {['النمو', 'المبيعات', 'المطاعم', 'الوجبات', 'الأوقات', 'المناطق', 'المستخدمين'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '10px 20px',
                borderRadius: '12px',
                border: activeTab !== tab ? '1px solid rgba(102, 106, 122, 0.22)' : 'none',
                backgroundColor: activeTab === tab ? '#E96A3A' : 'rgba(176, 179, 187, 0.13)',
                color: activeTab === tab ? '#FFFFFF' : '#666A7A',
                fontFamily: 'El Messiri, sans-serif',
                fontSize: '14px',
                fontWeight: activeTab === tab ? 700 : 500,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Growth Chart */}
        {(activeTab === 'النمو' || activeTab === '') && (
        <div style={{ 
          backgroundColor: '#FFFFFF', 
          borderRadius: '16px', 
          padding: '28px',
          border: '1px solid rgba(233, 106, 58, 0.15)',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
          boxSizing: 'border-box'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ 
              fontFamily: 'El Messiri, sans-serif', 
              fontSize: '16px', 
              fontWeight: 700, 
              color: '#2B2F3F',
              margin: 0,
              textAlign: 'right'
            }}>
              نمو الحسابات
            </h3>
            <button
              onClick={() => exportToCSV(growthData, 'نمو_الحسابات')}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid #E96A3A',
                backgroundColor: 'transparent',
                color: '#E96A3A',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              تصدير CSV
            </button>
          </div>
          {loading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px',
              color: '#666A7A',
              fontFamily: 'Cairo, sans-serif'
            }}>
              جاري التحميل...
            </div>
          ) : growthData.length === 0 ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px',
              color: '#666A7A',
              fontFamily: 'Cairo, sans-serif'
            }}>
              لا توجد بيانات
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={growthData}>
                <defs>
                  <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3FB984" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#3FB984" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorRestaurants" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#E96A3A" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#E96A3A" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorStores" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4A90E2" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#4A90E2" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#DCE3EB" />
                <XAxis dataKey="month" stroke="#7D7D7D" tick={{ fill: '#7D7D7D', fontSize: '12px', fontFamily: 'Cairo, sans-serif' }} />
                <YAxis stroke="#7D7D7D" tick={{ fill: '#7D7D7D', fontSize: '12px', fontFamily: 'Cairo, sans-serif' }} />
                <Tooltip 
                  contentStyle={{
                    fontFamily: 'Cairo, sans-serif',
                    borderRadius: '8px',
                    border: '1px solid #E0E0E0'
                  }}
                />
                <Legend 
                  wrapperStyle={{ fontFamily: 'Cairo, sans-serif', fontSize: '12px' }}
                />
                <Area type="monotone" dataKey="users" stackId="1" stroke="#3FB984" fill="url(#colorUsers)" name="المستخدمين" />
                <Area type="monotone" dataKey="restaurants" stackId="1" stroke="#E96A3A" fill="url(#colorRestaurants)" name="المطاعم" />
                <Area type="monotone" dataKey="stores" stackId="1" stroke="#4A90E2" fill="url(#colorStores)" name="المتاجر" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        )}

        {/* Sales Chart */}
        {activeTab === 'المبيعات' && (
        <div style={{ 
          backgroundColor: '#FFFFFF', 
          borderRadius: '16px', 
          padding: '28px',
          border: '1px solid rgba(233, 106, 58, 0.15)',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
          boxSizing: 'border-box'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ 
              fontFamily: 'El Messiri, sans-serif', 
              fontSize: '16px', 
              fontWeight: 700, 
              color: '#2B2F3F',
              margin: 0,
              textAlign: 'right'
            }}>
              المبيعات
            </h3>
            <button
              onClick={() => exportToCSV(salesData, 'المبيعات')}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid #E96A3A',
                backgroundColor: 'transparent',
                color: '#E96A3A',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              تصدير CSV
            </button>
          </div>
          {loading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px',
              color: '#666A7A',
              fontFamily: 'Cairo, sans-serif'
            }}>
              جاري التحميل...
            </div>
          ) : salesData.length === 0 ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px',
              color: '#666A7A',
              fontFamily: 'Cairo, sans-serif'
            }}>
              لا توجد بيانات
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={salesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#DCE3EB" />
                <XAxis dataKey="month" stroke="#7D7D7D" tick={{ fill: '#7D7D7D', fontSize: '12px', fontFamily: 'Cairo, sans-serif' }} />
                <YAxis stroke="#7D7D7D" tick={{ fill: '#7D7D7D', fontSize: '12px', fontFamily: 'Cairo, sans-serif' }} />
                <Tooltip 
                  contentStyle={{
                    fontFamily: 'Cairo, sans-serif',
                    borderRadius: '8px',
                    border: '1px solid #E0E0E0'
                  }}
                  formatter={(value) => {
                    if (typeof value === 'number') {
                      return value.toLocaleString('ar-SA');
                    }
                    return value;
                  }}
                />
                <Legend 
                  wrapperStyle={{ fontFamily: 'Cairo, sans-serif', fontSize: '12px' }}
                />
                <Bar dataKey="sales" fill="#E96A3A" name="المبيعات (ريال)" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        )}

        {/* Top Restaurants */}
        {activeTab === 'المطاعم' && (
        <div style={{ 
          backgroundColor: '#FFFFFF', 
          borderRadius: '16px', 
          padding: '28px',
          border: '1px solid rgba(233, 106, 58, 0.15)',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
          boxSizing: 'border-box'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ 
              fontFamily: 'El Messiri, sans-serif', 
              fontSize: '16px', 
              fontWeight: 700, 
              color: '#2B2F3F',
              margin: 0,
              textAlign: 'right'
            }}>
              أفضل المطاعم
            </h3>
            <button
              onClick={() => exportToCSV(topRestaurants, 'أفضل_المطاعم')}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid #E96A3A',
                backgroundColor: 'transparent',
                color: '#E96A3A',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              تصدير CSV
            </button>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666A7A' }}>جاري التحميل...</div>
          ) : topRestaurants.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666A7A' }}>لا توجد بيانات</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topRestaurants}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#E96A3A" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        )}

        {/* Top Meals */}
        {activeTab === 'الوجبات' && (
        <div style={{ 
          backgroundColor: '#FFFFFF', 
          borderRadius: '16px', 
          padding: '28px',
          border: '1px solid rgba(233, 106, 58, 0.15)',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
          boxSizing: 'border-box'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ 
              fontFamily: 'El Messiri, sans-serif', 
              fontSize: '16px', 
              fontWeight: 700, 
              color: '#2B2F3F',
              margin: 0,
              textAlign: 'right'
            }}>
              أفضل الوجبات
            </h3>
            <button
              onClick={() => exportToCSV(topMeals, 'أفضل_الوجبات')}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid #E96A3A',
                backgroundColor: 'transparent',
                color: '#E96A3A',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              تصدير CSV
            </button>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666A7A' }}>جاري التحميل...</div>
          ) : topMeals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666A7A' }}>لا توجد بيانات</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topMeals}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="quantity" fill="#3FB984" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        )}

        {/* Peak Hours */}
        {activeTab === 'الأوقات' && (
        <div style={{ 
          backgroundColor: '#FFFFFF', 
          borderRadius: '16px', 
          padding: '28px',
          border: '1px solid rgba(233, 106, 58, 0.15)',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
          boxSizing: 'border-box'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ 
              fontFamily: 'El Messiri, sans-serif', 
              fontSize: '16px', 
              fontWeight: 700, 
              color: '#2B2F3F',
              margin: 0,
              textAlign: 'right'
            }}>
              أوقات الذروة
            </h3>
            <button
              onClick={() => exportToCSV(peakHours.map(h => ({ ساعة: h.hour, عدد_الطلبات: h.count })), 'أوقات_الذروة')}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid #E96A3A',
                backgroundColor: 'transparent',
                color: '#E96A3A',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              تصدير CSV
            </button>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666A7A' }}>جاري التحميل...</div>
          ) : peakHours.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666A7A' }}>لا توجد بيانات</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={peakHours.sort((a, b) => a.hour - b.hour)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" label={{ value: 'الساعة', position: 'insideBottom', offset: -5 }} />
                <YAxis label={{ value: 'عدد الطلبات', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#E96A3A" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        )}

        {/* Top Areas */}
        {activeTab === 'المناطق' && (
        <div style={{ 
          backgroundColor: '#FFFFFF', 
          borderRadius: '16px', 
          padding: '28px',
          border: '1px solid rgba(233, 106, 58, 0.15)',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
          boxSizing: 'border-box'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ 
              fontFamily: 'El Messiri, sans-serif', 
              fontSize: '16px', 
              fontWeight: 700, 
              color: '#2B2F3F',
              margin: 0,
              textAlign: 'right'
            }}>
              أفضل المناطق
            </h3>
            <button
              onClick={() => exportToCSV(topAreas, 'أفضل_المناطق')}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid #E96A3A',
                backgroundColor: 'transparent',
                color: '#E96A3A',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              تصدير CSV
            </button>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666A7A' }}>جاري التحميل...</div>
          ) : topAreas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666A7A' }}>لا توجد بيانات</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={topAreas}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {topAreas.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        )}

        {/* Active Users */}
        {activeTab === 'المستخدمين' && (
        <div style={{ 
          backgroundColor: '#FFFFFF', 
          borderRadius: '16px', 
          padding: '28px',
          border: '1px solid rgba(233, 106, 58, 0.15)',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
          boxSizing: 'border-box'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ 
              fontFamily: 'El Messiri, sans-serif', 
              fontSize: '16px', 
              fontWeight: 700, 
              color: '#2B2F3F',
              margin: 0,
              textAlign: 'right'
            }}>
              المستخدمون النشطون
            </h3>
            <button
              onClick={() => exportToCSV(activeUsers.map(u => ({ الاسم: u.name, البريد: u.email, عدد_الطلبات: u.ordersCount, إجمالي_الإنفاق: u.totalSpent })), 'المستخدمون_النشطون')}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid #E96A3A',
                backgroundColor: 'transparent',
                color: '#E96A3A',
                fontFamily: 'Cairo, sans-serif',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              تصدير CSV
            </button>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666A7A' }}>جاري التحميل...</div>
          ) : activeUsers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666A7A' }}>لا توجد بيانات</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Cairo, sans-serif' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                    <th style={{ padding: '12px', textAlign: 'right', fontFamily: 'El Messiri, sans-serif', fontSize: '14px', fontWeight: 700, color: '#2B2F3F' }}>الاسم</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontFamily: 'El Messiri, sans-serif', fontSize: '14px', fontWeight: 700, color: '#2B2F3F' }}>البريد</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontFamily: 'El Messiri, sans-serif', fontSize: '14px', fontWeight: 700, color: '#2B2F3F' }}>عدد الطلبات</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontFamily: 'El Messiri, sans-serif', fontSize: '14px', fontWeight: 700, color: '#2B2F3F' }}>إجمالي الإنفاق</th>
                  </tr>
                </thead>
                <tbody>
                  {activeUsers.map((user) => (
                    <tr key={user.id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#2B2F3F' }}>{user.name}</td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#2B2F3F' }}>{user.email}</td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#2B2F3F' }}>{user.ordersCount}</td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#2B2F3F', fontWeight: 600 }}>
                        {new Intl.NumberFormat('ar-SA', { style: 'currency', currency: 'SAR' }).format(user.totalSpent)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}
      </div>
      <ToastContainer />
    </div>
  );
};

export default التقاريروالإحصائيات;

